import { LoadingButton } from '@mui/lab';
import { ButtonProps } from '@mui/material/Button';
import { styled } from '@mui/material';
import theme from '../../../styles/theme';

export interface IFormButtonProps {
  variant?: BtnType;
  [key: string]: any;
}

type BtnType = 'primary' | 'secondary' | 'outlined' | 'ghost' | 'green';
type BtnSize = 's' | 'm';
type StyledButtonProps = ButtonProps & IFormButtonProps;

const StyledLoadingButton = styled(LoadingButton)<StyledButtonProps>(
  ({ bgcolor, hovercolor, fontcolor, spinnercolor, padding, fontSize, bordercolor }) => ({
    fontFamily: 'Montserrat',
    fontSize: fontSize,
    fontWeight: 600,
    textTransform: 'none',
    cursor: 'pointer',
    borderRadius: '8px',
    padding,
    color: fontcolor,
    backgroundColor: bgcolor,
    transition: 'background-color 150ms linear',
    borderWidth: '2px',
    borderColor: bordercolor || 'transparent',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: hovercolor,
      borderColor: bordercolor || 'transparent',
      transition: 'all 120ms linear',
      borderWidth: '2px',
    },

    '& .MuiLoadingButton-loadingIndicator': {
      color: spinnercolor || theme.custom.palette.primary.white,
    },

    '& .MuiButton-startIcon,.MuiButton-endIcon': {
      '& .MuiSvgIcon-fontSizeMedium': {
        fontSize: '16px',
      },
    },

    '&.Mui-disabled': {
      borderWidth: '2px',
      borderColor: bordercolor || 'transparent',
    },
  })
);

const getStylesByType = (type: BtnType) => {
  const style = {
    primary: {
      fontcolor: theme.custom.palette.primary.white,
      bgcolor: theme.custom.palette.primary.default,
      hovercolor: theme.custom.palette.primary.hover,
    },
    secondary: {
      fontcolor: '#757575',
      bgcolor: '#f2f5f9',
      hovercolor: '#f0f0f0',
    },
    outlined: {
      bgcolor: 'transparent',
      bordercolor: theme.custom.palette.primary.default,
      fontcolor: theme.custom.palette.primary.default,
      spinnercolor: theme.custom.palette.primary.default,
    },
    ghost: {
      bgColor: 'transparent',
      hoverColor: theme.custom.palette.primary.lightgray,
    },
    green: {
      fontcolor: theme.custom.palette.primary.white,
      bgcolor: '#00B031',
      hovercolor: '#73c85d',
      weight: 700,
    },
  };
  return style[type] || style['primary'];
};

const getStylesBySize = (size: BtnSize) => {
  const style = {
    s: {
      padding: '2px 8px',
      fontSize: '14px',
      outlinewidth: '2px',
    },
    m: {
      padding: '7px 17px',
      fontSize: '0.875rem',
    },
  };

  return style[size] || style['m'];
};

const FormButton: React.FC<IFormButtonProps> = ({
  children,
  variant = 'primary',
  endIcon,
  startIcon,
  onClick,
  disabled = false,
  isLoading = false,
  submit,
  size = 'm',
  ...other
}) => {
  const variantProps = getStylesByType(variant);
  const sizeProps = getStylesBySize(size);

  return (
    <StyledLoadingButton
      loading={isLoading}
      variant={'outlined'}
      {...variantProps}
      {...sizeProps}
      {...other}
      endIcon={endIcon}
      startIcon={startIcon}
      onClick={onClick}
      disabled={isLoading || disabled}
      type={submit ? 'submit' : 'button'}
    >
      {children}
    </StyledLoadingButton>
  );
};

export default FormButton;
